// src/FreelancerProfile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, Chip, Button, Stack, Divider,
  List, ListItem, ListItemText, CircularProgress, Tooltip, Dialog,
  Alert, Snackbar, GlobalStyles
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import StyleIcon from '@mui/icons-material/Style';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import TimelineIcon from '@mui/icons-material/Timeline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import FreelancerAlertsPanel from './FreelancerAlertsPanel';
import EditProfileForm from './EditProfileForm';
import CreateMaestranzaForm from './CreateMaestranzaForm';
import EditMaestranzaForm from './EditMaestranzaForm';
import { fetchJson, API_BASE } from './api';
import { useTranslation } from 'react-i18next';
import FreelancerCalendar from './FreelancerCalendar';
import { busyDaysFromProduzioni } from './lib/calendarUtils';
import Paper from '@mui/material/Paper';
import AvailabilityCalendar from './AvailabilityCalendar';
import Grid from '@mui/material/Grid';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import ProfileCoachPanel from './components/ProfileCoachPanel';
import ProfileCoachAI from './components/ProfileCoachAI';
import SquadraModal from './SquadraModal';

// Attiva/disattiva il coach AI senza toccare altro
const USE_AI_COACH = true;


// const DEBUG_API_BASE = true;
// Percorre path tipo "data.produzioni" e ritorna il valore
const getAnyPath = (obj, path) => {
  try {
    return path.split('.').reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj);
  } catch { return undefined; }
};

// Scansione “intelligente”: trova il primo array di oggetti che sembra elenco produzioni (ha chiavi data)
const findFirstArrayWithDates = (obj) => {
  const seen = new Set();
  const stack = [obj];
  const dateKeys = new Set([
    'start_date','end_date','data_inizio','data_fine',
    'data_inizio_contratto','data_fine_contratto','from','to','inizio','fine'
  ]);
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    if (Array.isArray(cur)) {
      if (cur.length && typeof cur[0] === 'object') {
        const keys = Object.keys(cur[0] || {});
        if (keys.some(k => dateKeys.has(k))) return cur;
      }
    } else {
      for (const v of Object.values(cur)) stack.push(v);
    }
  }
  return [];
};


// Helper: ritorna il primo array non-vuoto tra le chiavi indicate
const pickFirstArray = (o, keys) => {
  if (!o) return [];
  for (const k of keys) {
    const v = o?.[k];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  return [];
};



// 🔥 VERSIONE DEFINITIVA — carica SOLO le produzioni del freelance loggato
async function fetchMyProductionsSmart(fetchJson) {
  try {
    const me = await fetchJson('freelancers/self');
    // usa maestranze già incluse in /self se disponibili
    if (Array.isArray(me?.maestranze) && me.maestranze.length > 0) {
      console.log('🎯 Productions source: /freelancers/self.maestranze', me.maestranze.length);
      return me.maestranze;
    }
    // fallback: usa freelancer_id (non user_id) per la route /:id
    const freelancerId = me?.freelancer_id;
    if (!freelancerId) return [];
    const full = await fetchJson(`/freelancers/${freelancerId}`);
    const arr = Array.isArray(full?.maestranze) ? full.maestranze : [];
    console.log('🎯 Productions source: /freelancers/:id.maestranze', arr.length);
    return arr;
  } catch (err) {
    console.error('❌ Errore fetchMyProductionsSmart', err);
    return [];
  }
}


// Riconosce se una produzione appartiene al freelance loggato (id/email/nome),
// anche se l'informazione è annidata in array come assignees/crew/members ecc.
function belongsToMe(prod, me) {
  if (!prod || !me) return false;

  const ids = new Set(
    [me.id, me._id, me.uuid, me.user_id, me.userId]
      .filter(v => v !== undefined && v !== null)
      .map(String)
  );

  const emails = new Set(
    [me.email, me.mail]
      .filter(Boolean)
      .map(s => String(s).toLowerCase())
  );

  const names = new Set(
    [
      `${me.nome || ''} ${me.cognome || ''}`.trim().toLowerCase(),
      `${me.cognome || ''} ${me.nome || ''}`.trim().toLowerCase(),
    ].filter(s => s && s !== '')
  );

  // 1) chiavi ID/email direttamente sul record produzione
  const directIdKeys = [
    'freelancer_id','freelancerId','maestranza_id','maestranzaId','user_id','userId','editor_id'
  ];
  for (const k of directIdKeys) {
    const v = prod?.[k];
    if (v != null && ids.has(String(v))) return true;
  }

  const directEmailKeys = ['freelancer_email','user_email','editor_email','email'];
  for (const k of directEmailKeys) {
    const v = prod?.[k];
    if (v && emails.has(String(v).toLowerCase())) return true;
  }

  // 2) array di partecipanti/assegnati in strutture comuni
  const arrayKeys = [
    'assignees','freelancers','crew','maestranze','team','members','assignments','roles','editors','people'
  ];
  for (const a of arrayKeys) {
    const arr = prod?.[a];
    if (!Array.isArray(arr)) continue;

    for (const m of arr) {
      const mid = m?.id || m?._id || m?.freelancer_id || m?.freelancerId || m?.maestranza_id || m?.user_id || m?.userId;
      if (mid != null && ids.has(String(mid))) return true;

      const memail = (m?.email || m?.mail || m?.freelancer_email || m?.user_email || '').toLowerCase();
      if (memail && emails.has(memail)) return true;

      const mname = `${m?.nome || ''} ${m?.cognome || ''}`.trim().toLowerCase();
      if (mname && names.has(mname)) return true;
    }
  }

  // ❌ niente fallback su owner_type: potrebbe far passare voci non personali
  return false;
}




// Legge il valore dalla prima chiave disponibile
const pick = (obj, keys, fallback='') => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
};

const formatDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d) ? String(s) : d.toLocaleDateString('it-IT');
};


function normalizeProduction(p = {}) {
  const id =
    p.id ?? p._id ?? p.uuid ?? p.ID ?? p.codice ?? null;

  // titoli / nomi show
  const title =
    p.title ?? p.titolo ?? p.nome ?? p.name ?? p.program_name ?? p.produzione ?? '';

  // cliente / network / company
  const client =
    p.client ?? p.cliente ?? p.network ?? p.azienda ?? p.company ?? '';

  // ruolo
  const role =
    p.role ?? p.ruolo ?? p.mansione ?? '';

  // date inizio/fine (includi anche quelle della tabella MAESTRANZE)
  const start =
    p.data_inizio_contratto ??
    p.start_date ?? p.data_inizio ?? p.inizio ?? p.start ?? p.da ?? p.from ?? null;
   const end =
    p.data_fine_contratto ??
    p.end_date ?? p.data_fine ?? p.fine ?? p.end ?? p.a ?? p.to ?? null;

  // restituisco con TUTTE le alias più usate:
  return {
    // chiavi “canoniche”
    id,
    title,
    client,
    role,
    start_date: start,
    end_date: end,

    // alias che il JSX potrebbe aspettarsi
    titolo: title,
    cliente: client,
    ruolo: role,
    data_inizio: start,
    data_fine: end,
    // alias per compatibilità con EditMaestranzaForm
    data_inizio_contratto: start,
    data_fine_contratto: end,
    // campi aggiuntivi
    produzione: p.produzione ?? title,
    azienda: p.azienda ?? client,
    is_tentative: p.is_tentative || false,
  };
}


// === Palette colori condivisa (calendario, legenda, statistiche) ===
const CAL_COLORS = {
  freeBg:   'rgba(74,222,128,0.1)',   freeText:   '#4ade80', // LIBERO (verde)
  busyBg:   'rgba(248,113,113,0.1)',  busyText:   '#f87171', // OCCUPATO (rosso)
  workedBg: 'rgba(212,165,80,0.12)',  workedText: '#d4a550', // LAVORATO (gold)
  outBg:    'rgba(255,255,255,0.03)', outText:    '#4a4a5a', // altro mese
  todayRing:'#d4a550',
};





// ---------- helpers ----------
const formatPhone = (raw) => {
  const d = (raw || '').replace(/[^\d]/g, '');
  if (d.length < 7) return d;
  const a = d.slice(0,3), b = d.slice(3,5), c = d.slice(5,7), e = d.slice(7);
  return [a,b,c,e].filter(Boolean).join('.');
};

const resolveProfileImageUrl = (raw, version = Date.now()) => {
  if (!raw) return undefined;
  if (/^https?:\/\//i.test(raw)) return `${raw}?v=${version}`;
  if (raw.startsWith('/')) return `${API_BASE}${raw}?v=${version}`;
  return `${API_BASE}/${raw.replace(/^\/+/, '')}?v=${version}`;
};

const displayNameOf = (f) => {
  const nome = f?.nome?.trim() || '';
  const cognome = f?.cognome?.trim() || '';
  const full = [nome, cognome].filter(Boolean).join(' ');
  if (full) return full;
  return f?.email?.split('@')[0] || 'Profilo';
};

// YYYY-MM-DD safe (no slittamenti)
const toDateOnly = (s) => {
  if (!s) return '';
  const str = String(s);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    let y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
    // ISO midnight UTC -> shift +1 if local tz ahead
    if (/T00:00:00(.000)?Z$/.test(str)) {
      const tz = new Date().getTimezoneOffset(); // Rome: -60/-120
      if (tz <= -60) {
        const dt = new Date(Date.UTC(y, mo - 1, d));
        dt.setUTCDate(dt.getUTCDate() + 1);
        y  = dt.getUTCFullYear();
        mo = dt.getUTCMonth() + 1;
        d  = dt.getUTCDate();
      }
    }
    const mm = String(mo).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }
  const d = new Date(str);
  if (isNaN(d)) return '';
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
};

const formatDateIT = (s) => {
  const ymd = toDateOnly(s);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

const sortByEndDesc = (a, b) => {
  const aYmd = toDateOnly(
    a?.end_date || a?.data_fine || a?.data_fine_contratto || a?.fine || a?.to
  ) || '';
  const bYmd = toDateOnly(
    b?.end_date || b?.data_fine || b?.data_fine_contratto || b?.fine || b?.to
  ) || '';
  if (aYmd === bYmd) return 0;
  return aYmd < bYmd ? 1 : -1; // desc
};

// Converte un oggetto Date in 'YYYY-MM-DD' senza timezone shift
const toYmdLocal = (date) => {
  if (!(date instanceof Date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};


// Piccoli token UI locali
const UI = {
  radius: 3,
  shadow: '0 6px 24px rgba(0,0,0,0.12)',
};

function toMaestranzaModel(p = {}) {
  const id =
    p.id ?? p._id ?? p.uuid ?? p.code ?? null;

  const azienda =
    p.azienda ?? p.client ?? p.cliente ?? p.company ?? p.network ?? '';

  const produzione =
    p.produzione ?? p.titolo ?? p.title ?? p.nome ?? p.name ?? '';

  const ruoli = Array.isArray(p.ruoli)
    ? p.ruoli
    : (p.ruolo || p.role || p.mansione ? [p.ruolo || p.role || p.mansione] : []);

  const ruolo = ruoli[0] || '';

  const data_inizio_contratto =
    p.data_inizio_contratto ??
    p.start_date ?? p.data_inizio ?? p.inizio ?? p.start ?? p.da ?? p.from ?? '';

  const data_fine_contratto =
    p.data_fine_contratto ??
    p.end_date ?? p.data_fine ?? p.fine ?? p.end ?? p.a ?? p.to ?? '';

  return {
    id,
    azienda,
    produzione,
    ruoli,
    is_tentative: p.is_tentative || false,
    ruolo,
    data_inizio_contratto,
    data_fine_contratto,
  };
}

// === Durata formattata ===
const IT_MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const EN_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function labelRange(startIso, endIso, lang = 'it') {
  if (!startIso && !endIso) return '';
  const toD = (s)=> (s ? new Date(s) : null);
  const s = toD(startIso), e = toD(endIso) || s;
  if (!s && !e) return '';
  const MONTHS = lang === 'en' ? EN_MONTHS : IT_MONTHS;
  const y1 = (s||e).getFullYear(), m1 = MONTHS[(s||e).getMonth()];
  const y2 = e.getFullYear(),      m2 = MONTHS[e.getMonth()];
  if (s && e) {
    const sameYear = y1 === y2;
    const sameMonth = sameYear && s.getMonth() === e.getMonth();
    if (sameMonth) return `${m1} ${y1}`;
    return sameYear ? `${m1}–${m2} ${y1}` : `${m1} ${y1} – ${m2} ${y2}`;
  }
  return `${m2} ${y2}`;
}




// ─────────────────────────────────────────────────────────────
// NewsBanner — banner dismissibile con novità piattaforma
// ─────────────────────────────────────────────────────────────
function NewsBanner() {
  const { t } = useTranslation();
  const NEWS_KEY = 'jkl_news_dismissed_v2';
  const [visible, setVisible] = React.useState(() => {
    try { return !localStorage.getItem(NEWS_KEY); } catch { return true; }
  });
  if (!visible) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: { xs: 1.5, sm: 2 }, py: 1,
      borderRadius: '10px',
      background: 'rgba(212,165,80,0.07)',
      border: '1px solid rgba(212,165,80,0.22)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ fontSize: 16, flexShrink: 0, color: '#d4a550' }}>✦</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#d4a550', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {t('profile.news_date')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'rgba(240,236,228,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
            {t('profile.news_text')}
          </Typography>
        </Box>
      </Box>
      <Box
        onClick={() => { try { localStorage.setItem(NEWS_KEY, '1'); } catch {} setVisible(false); }}
        sx={{ ml: 2, cursor: 'pointer', color: 'rgba(212,165,80,0.4)', fontSize: 22, lineHeight: 1, flexShrink: 0, '&:hover': { color: '#d4a550' } }}
      >
        ×
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// NetworkBanner — strip statistiche del network professionale
// Riceve dati già caricati, nessuna chiamata API aggiuntiva
// ─────────────────────────────────────────────────────────────

function BachecaBoard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [category, setCategory] = React.useState('tutti');
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ category: 'disponibilita', title: '', body: '', is_anonymous: false });
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const CATS = [
    { id: 'tutti', label: t('profile.cat_all') },
    { id: 'disponibilita', label: t('profile.cat_availability') },
    { id: 'lavoro', label: t('profile.cat_work') },
    { id: 'logistica', label: t('profile.cat_logistics') },
    { id: 'varie', label: t('profile.cat_misc') },
  ];

  const CAT_COLORS = {
    disponibilita: '#4ade80', lavoro: '#60a5fa',
    logistica: '#f59e0b', varie: '#a78bfa',
  };

  const fetchPosts = React.useCallback(() => {
    setLoading(true);
    const url = category === 'tutti' ? `${API}/board` : `${API}/board?category=${category}`;
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => { setPosts(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [category, token]);

  React.useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/board`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        setShowForm(false);
        setForm({ category: 'disponibilita', title: '', body: '', is_anonymous: false });
        setMsg(t('profile.bacheca_saved'));
        fetchPosts();
        setTimeout(() => setMsg(null), 3000);
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('profile.bacheca_confirm_delete'))) return;
    await fetch(`${API}/board/${id}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
    setPosts(p => p.filter(x => x.id !== id));
  };

  const handleMessage = async (userId) => {
    try {
      const r = await fetch(`${API}/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ otherUserId: userId }),
      });
      const d = await r.json();
      if (d?.id) window.location.href = `/messages?cid=${d.id}`;
    } catch {}
  };

  const daysLeft = (exp) => {
    const d = Math.ceil((new Date(exp) - new Date()) / 86400000);
    return d > 0 ? d : 0;
  };

  const GOLD = '#d4a550';
  const BORDER = 'rgba(255,255,255,0.07)';
  const BG = '#0f0f17';

  return (
    <Box sx={{ height: 350, width: '100%', display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', p: 1.5, background: 'rgba(255,255,255,0.02)', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: GOLD }}>
          {t('profile.bacheca_title')}
        </Typography>
        <Box onClick={() => setShowForm(v => !v)}
          sx={{ fontSize: 11, fontWeight: 700, color: GOLD, cursor: 'pointer', border: '1px solid rgba(212,165,80,0.3)', borderRadius: '6px', px: 1.5, py: 0.5, '&:hover': { background: 'rgba(212,165,80,0.08)' } }}>
          {t('profile.bacheca_publish')}
        </Box>
      </Box>

      {/* Form */}
      {showForm && (
        <Box sx={{ background: BG, border: '1px solid rgba(212,165,80,0.25)', borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            style={{ background: '#1a1a28', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#f0ece4', fontSize: 12, padding: '6px 10px', fontFamily: 'Kanit, sans-serif' }}>
            {CATS.filter(c => c.id !== 'tutti').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input placeholder={t("profile.bacheca_ph_title")} maxLength={120} value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            style={{ background: '#1a1a28', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#f0ece4', fontSize: 12, padding: '6px 10px', fontFamily: 'Kanit, sans-serif', outline: 'none' }} />
          <textarea placeholder={t("profile.bacheca_ph_body")} rows={3} value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            style={{ background: '#1a1a28', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#f0ece4', fontSize: 12, padding: '6px 10px', fontFamily: 'Kanit, sans-serif', resize: 'vertical', outline: 'none' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setForm(f => ({ ...f, is_anonymous: !f.is_anonymous }))}>
              <Box sx={{ width: 14, height: 14, border: '1px solid rgba(212,165,80,0.4)', borderRadius: '3px', background: form.is_anonymous ? GOLD : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {form.is_anonymous && <Box sx={{ fontSize: 9, color: '#07070a', fontWeight: 900 }}>✓</Box>}
              </Box>
              <Typography sx={{ fontSize: 11, color: 'rgba(240,236,228,0.5)' }}>{t('profile.bacheca_anon')}</Typography>
            </Box>
            <Typography sx={{ fontSize: 9, color: 'rgba(240,236,228,0.3)', letterSpacing: '0.05em' }}>{t('profile.bacheca_expiry')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box onClick={() => setShowForm(false)}
              sx={{ flex: 1, textAlign: 'center', py: 0.75, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(240,236,228,0.4)', fontSize: 12, cursor: 'pointer' }}>
              {t('profile.bacheca_cancel')}
            </Box>
            <Box onClick={handleSubmit}
              sx={{ flex: 2, textAlign: 'center', py: 0.75, borderRadius: '6px', background: 'rgba(212,165,80,0.12)', border: '1px solid rgba(212,165,80,0.3)', color: GOLD, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? t('profile.bacheca_saving') : t('profile.bacheca_submit')}
            </Box>
          </Box>
        </Box>
      )}

      {msg && <Typography sx={{ fontSize: 11, color: '#4ade80', textAlign: 'center' }}>{msg}</Typography>}

      {/* Tabs categorie */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {CATS.map(c => (
          <Box key={c.id} onClick={() => setCategory(c.id)}
            sx={{ fontSize: 10, fontWeight: 700, px: 1.25, py: 0.4, borderRadius: '20px', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase',
              background: category === c.id ? 'rgba(212,165,80,0.12)' : 'transparent',
              border: category === c.id ? '1px solid rgba(212,165,80,0.3)' : '1px solid rgba(255,255,255,0.06)',
              color: category === c.id ? GOLD : 'rgba(240,236,228,0.4)',
            }}>
            {c.label}
          </Box>
        ))}
      </Box>

      {/* Lista post */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: 4 },
      }}>
        {loading ? (
          <Typography sx={{ fontSize: 12, color: 'rgba(240,236,228,0.3)', textAlign: 'center', mt: 3 }}>{t('profile.bacheca_loading')}</Typography>
        ) : posts.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography sx={{ fontSize: 28, mb: 1, opacity: 0.2 }}>📌</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(240,236,228,0.25)', textAlign: 'center', lineHeight: 1.7 }}>{t('profile.bacheca_empty_line1')}<br/>{t('profile.bacheca_empty_line2')}<br/>{t('profile.bacheca_empty_sub')}</Typography>
          </Box>
        ) : posts.map(post => {
          const col = CAT_COLORS[post.category] || GOLD;
          const authorName = post.is_anonymous ? t('profile.bacheca_anon_user') : [post.nome, post.cognome].filter(Boolean).join(' ') || t('profile.bacheca_anon_user');
          return (
            <Box key={post.id} sx={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: '8px', p: '8px 10px', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box sx={{ fontSize: 8, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: '20px', background: `${col}18`, border: `1px solid ${col}40`, color: col, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                    {CATS.find(c => c.id === post.category)?.label || post.category}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: 'rgba(240,236,228,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {authorName}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 9, color: 'rgba(240,236,228,0.2)', flexShrink: 0 }}>
                  {new Date(post.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: 'short' })}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#f0ece4', lineHeight: 1.2 }}>{post.title}</Typography>
              <Typography sx={{ fontSize: 10, color: 'rgba(240,236,228,0.45)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{post.body}</Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.25 }}>
                {post.is_mine ? (
                  <Box onClick={() => handleDelete(post.id)}
                    sx={{ fontSize: 10, color: 'rgba(248,113,113,0.5)', cursor: 'pointer', '&:hover': { color: '#f87171' } }}>
                    {t('profile.bacheca_delete')}
                  </Box>
                ) : (
                  <Box onClick={() => handleMessage(post.user_id)}
                    sx={{ fontSize: 10, fontWeight: 700, color: GOLD, cursor: 'pointer', border: '1px solid rgba(212,165,80,0.25)', borderRadius: '6px', px: 1.25, py: 0.4, '&:hover': { background: 'rgba(212,165,80,0.08)' } }}>
                    {t('profile.bacheca_write')}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function BachecaPlaceholder() {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: "100%", minHeight: 220, borderRadius: "10px", border: "1px dashed rgba(212,165,80,0.22)", background: "rgba(212,165,80,0.025)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, p: 3 }}>
      <Box sx={{ fontSize: 28, opacity: 0.3 }}>📌</Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(212,165,80,0.5)", letterSpacing: "0.12em", textTransform: "uppercase" }}>{t('profile.bacheca_placeholder_title')}</Typography>
      <Typography sx={{ fontSize: 11, color: "rgba(240,236,228,0.25)", textAlign: "center", maxWidth: 200, lineHeight: 1.6 }}>{t('profile.bacheca_placeholder_sub')}</Typography>
    </Box>
  );
}

function StatsBanner({ produzioni, workedDays, freeDays, occupiedDays, nextGapDate, profileViews }) {
  const { t } = useTranslation();
  const uniqueCompanies = React.useMemo(() => {
    const s = new Set(
      (produzioni || [])
        .map(p => p.azienda || p.client || p.cliente || p.company || p.network || '')
        .filter(Boolean)
    );
    return s.size;
  }, [produzioni]);

  const anniCarriera = React.useMemo(() => {
    const years = (produzioni || [])
      .map(p => {
        const raw = p.start_date || p.data_inizio || p.data_inizio_contratto;
        if (!raw) return null;
        const y = new Date(raw).getFullYear();
        return (y > 1980 && y <= new Date().getFullYear()) ? y : null;
      })
      .filter(Boolean);
    if (!years.length) return null;
    return new Date().getFullYear() - Math.min(...years);
  }, [produzioni]);

  const stats = [
    { label: t('profile.stats_productions'), value: (produzioni || []).length, color: '#d4a550' },
    { label: t('profile.stats_companies'), value: uniqueCompanies, color: '#60a5fa' },
    anniCarriera != null ? { label: t('profile.stats_career_years'), value: anniCarriera, color: '#4ade80' } : null,
    { label: t('profile.stats_worked_days'), value: workedDays, color: '#a78bfa' },
    { label: t('profile.stats_free_days'), value: freeDays, color: '#4ade80' },
    { label: t('profile.stats_booked_days'), value: occupiedDays, color: '#f87171' },
    nextGapDate ? { label: t('profile.stats_next_gap'), value: nextGapDate, color: '#d4a550', isText: true } : null,
    profileViews != null ? { label: t('profile.stats_profile_views'), value: profileViews + 'x', color: '#d4a550', isText: true } : null,
  ].filter(Boolean);

  return (
    <Box sx={{
      display: 'flex', gap: 1, flexWrap: 'wrap',
      p: { xs: 1.25, sm: 1.5 },
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.015)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      {stats.map(({ label, value, color }) => (
        <Box key={label} sx={{
          flex: '1 1 auto',
          minWidth: { xs: 'calc(50% - 8px)', sm: 100 },
          py: 1, px: 0.5,
          borderRadius: '8px',
          background: `${color}0d`,
          border: `1px solid ${color}28`,
          textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 400, color, lineHeight: 1, fontFamily: 'Kanit, sans-serif' }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: 'rgba(240,236,228,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.25, lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function FreelancerProfile({ showAlerts = true, onEditProfile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [me, setMe] = useState(null);
  const [produzioni, setProduzioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prodLoading, setProdLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProd, setEditProd] = useState(null);

  const [authError, setAuthError] = useState(false);
  const [visible, setVisible] = useState(true);
  const [snack, setSnack] = useState(null);
  const [profileViews, setProfileViews] = useState(null);
  const [creditsMap, setCreditsMap] = useState({});

  const overlineSx = {
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'text.secondary',
    textTransform: 'uppercase'
  };

 useEffect(() => {
  let alive = true;

  const load = async () => {
    setLoading(true);
    setProdLoading(true);

    try {
      // 1) profilo del freelance loggato (SENZA slash iniziale per evitare /api/api)
      const profilo = await fetchJson('freelancers/self');
      if (!alive) return;
      setMe(profilo);
      setVisible(!!profilo?.visible);
      // Carica visualizzazioni profilo del mese
      const fId = profilo?.freelancer_id || profilo?.id;
      if (fId) {
        fetchJson(`/freelancers/${fId}/views`)
          .then(d => setProfileViews(d?.count ?? 0))
          .catch(() => setProfileViews(0));
      }

      // === DIAGNOSTICA: stampa le chiavi che arrivano dal profilo ===
      try {
        console.warn('🔎 self keys:', Object.keys(profilo || {}));
        console.warn('🔎 potenziali array len:', {
          maestranze: Array.isArray(profilo?.maestranze) ? profilo.maestranze.length : 0,
          produzioni: Array.isArray(profilo?.produzioni) ? profilo.produzioni.length : 0,
          productions: Array.isArray(profilo?.productions) ? profilo.productions.length : 0,
          my_productions: Array.isArray(profilo?.my_productions) ? profilo.my_productions.length : 0,
          produzioni_personali: Array.isArray(profilo?.produzioni_personali) ? profilo.produzioni_personali.length : 0,
          assignments: Array.isArray(profilo?.assignments) ? profilo.assignments.length : 0,
          jobs: Array.isArray(profilo?.jobs) ? profilo.jobs.length : 0,
          crew: Array.isArray(profilo?.crew) ? profilo.crew.length : 0,
          projects: Array.isArray(profilo?.projects) ? profilo.projects.length : 0,
        });
      } catch {}

      // 2b) carico i crediti CV collegati (una volta sola) → map source_maestranza_id -> credit
      fetchJson('/cv/me/credits')
        .then(data => {
          if (!alive) return;
          const list = Array.isArray(data?.credits) ? data.credits : [];
          const map = {};
          list.forEach(c => {
            const ids = Array.isArray(c.source_maestranza_ids) && c.source_maestranza_ids.length
              ? c.source_maestranza_ids
              : c.source_maestranza_id != null ? [c.source_maestranza_id] : [];
            ids.forEach(id => { map[String(id)] = c; });
          });
          setCreditsMap(map);
        })
        .catch(() => { /* silenzioso: la timeline funziona anche senza crediti */ });

      // 2) carico le produzioni del mio freelance in modo robusto
let list = [];
try {
  // usa il fetch “intelligente” che prova più endpoint
  list = await fetchMyProductionsSmart(fetchJson);
} catch (e) {
  console.error('Errore caricando produzioni (smart):', e);
  list = [];
}

// 3) normalizza -> prova filtro “mio” -> fallback se svuota tutto -> ordina
const normalized = Array.isArray(list) ? list.map(normalizeProduction) : [];
const finalList  = normalized.sort(sortByEndDesc);

setProduzioni(finalList);

// diagnostica: quante prima/dopo
console.warn('📊 Productions (normalized / final):',
  normalized.length, finalList.length);


    } catch (e) {
      console.error(e);
      if (e?.status === 401 && alive) setAuthError(true);
    } finally {
      if (alive) {
        setProdLoading(false);
        setLoading(false);
      }
    }
  };

  load();
  return () => { alive = false; };
}, []);



// Calendar data (read-only)

// Sorgente robusta delle produzioni: me.produzioni oppure prop 'produzioni'
const rawProduzioni =
  (Array.isArray(produzioni) && produzioni.length > 0) ? produzioni :
  (Array.isArray(me?.produzioni) ? me.produzioni : []);

// === Helpers per date (senza slittamenti di fuso) ===
const toLocalDate = (val) => {
  if (!val) return null;
  const s = String(val);
  // "YYYY-MM-DD" o ISO con orario -> estraggo solo la parte di data
  const ymd = /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
  if (ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d); // locale (no UTC)
  }
  const dt = new Date(s);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

const ymdLocal = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`
    : '';

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const daysRangeToSet = (start, end) => {
  const set = new Set();
  if (!start) return set;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date(start);
  for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
    set.add(ymdLocal(d));
  }
  return set;
};

// === Costruisce il Set dei giorni occupati dalle "produzioni" ===
const buildBusySetFromProduzioni = (list = []) => {
  const set = new Set();
  list.filter(p => !p.is_tentative).forEach((p) => {
    const startRaw =
  p.start_date || p.data_inizio || p.data_inizio_contratto || p.inizio || p.start || p.da || p.from;
const endRaw =
  p.end_date   || p.data_fine   || p.data_fine_contratto   || p.fine   || p.end   || p.a   || p.to;


    const start = toLocalDate(startRaw);
    const end = toLocalDate(endRaw) || start;

    if (start) {
      const range = daysRangeToSet(start, end);
      range.forEach((day) => set.add(day));
    }
  });
  return set;
};

// Giorni OCCUPATI come Set('YYYY-MM-DD') – memoizzato
const busySet = useMemo(
  () => buildBusySetFromProduzioni(rawProduzioni),
  [rawProduzioni]
);

// --- STATISTICHE: calcoli base ---
const todayStr = new Date().toISOString().slice(0, 10);
const currentYear = new Date().getFullYear();
const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);

const occupiedDays = Array.from(busySet).filter(d => d >= todayStr).length; // occupati da oggi in poi
const totalDays    = isLeapYear(currentYear) ? 366 : 365;


function MiniMonthCalendar({ busySet }) {
  const today = new Date();
  today.setHours(0,0,0,0);

  const [monthAnchor, setMonthAnchor] = React.useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );

  const year  = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // lun=0 ... dom=6

  const lastOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastOfMonth.getDate();

  // solo giorni del mese corrente
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));


  const fmtYmd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const isSameDay = (a,b) =>
    a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();

  return (
    <Box sx={{ width: '100%' }}>
      {/* intestazione mese + frecce */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 0.5 }}>
        <IconButton size="small" onClick={()=>{
          const d = new Date(monthAnchor); d.setMonth(d.getMonth()-1); setMonthAnchor(d);
        }}>
          <ChevronLeftIcon />
        </IconButton>

        <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {monthAnchor.toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { month:'long', year:'numeric' })}
        </Typography>

        <IconButton size="small" onClick={()=>{
          const d = new Date(monthAnchor); d.setMonth(d.getMonth()+1); setMonthAnchor(d);
        }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* lettere dei giorni */}
      <Box sx={{
        display:'grid',
        gridTemplateColumns:'repeat(7, 1fr)',
        gap: 1,
        mb: 1,
        px: 0.5,
        color:'text.secondary',
        fontSize: 12,
      }}>
        {t('profile.cal_days', { returnObjects: true }).map((w,i)=>(
          <Box key={i} sx={{ textAlign:'center' }}>{w}</Box>
        ))}
      </Box>

      {/* griglia giorni */}
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap: 0.5 }}>
        {days.map((d, idx) => {
  const key = fmtYmd(d);

  // palette in base a busy/worked/free (niente "fuori mese")
  let bg = CAL_COLORS.freeBg;
  let fg = CAL_COLORS.freeText;

  if (busySet.has(key)) {
    if (d < today) { bg = CAL_COLORS.workedBg; fg = CAL_COLORS.workedText; } // passato = "lavorato"
    else           { bg = CAL_COLORS.busyBg;   fg = CAL_COLORS.busyText;   } // oggi/futuro = occupato
  }

  const isToday = isSameDay(d, today);
  const offsetStyle = idx === 0 ? { gridColumnStart: firstWeekday + 1 } : undefined;

  return (
    <Box
      key={key}
      sx={{
        height: 28,
        borderRadius: 1,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bg,
        color: fg,
        border: isToday ? `1.5px solid ${CAL_COLORS.todayRing}` : '1px solid #eee',
        fontWeight: 600,
        ...offsetStyle,
      }}
      title={key}
    >
      {d.getDate()}
    </Box>
  );
})}

      </Box>

      {/* legenda */}
      <Box sx={{ display:'flex', alignItems:'center', gap: 3, mt: 1.5 }}>
        {[
          { label:t('profile.cal_free'),   bg: CAL_COLORS.freeBg,   dot: CAL_COLORS.freeText   },
          { label:t('profile.cal_worked'), bg: CAL_COLORS.workedBg, dot: CAL_COLORS.workedText },
          { label:t('profile.cal_busy'),   bg: CAL_COLORS.busyBg,   dot: CAL_COLORS.busyText   },
        ].map((it)=>(
          <Box key={it.label} sx={{ display:'flex', alignItems:'center', gap: 1 }}>
            <Box sx={{
              width:10, height:10, borderRadius:'3px',
              bgcolor: it.bg, border:`1px solid ${it.dot}`
            }}/>
            <Typography variant="caption" sx={{ color:'text.secondary' }}>{it.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}




// ===== Statistiche anno corrente e “prossimo buco” =====
const now = new Date();

const { workedDays, freeDays } = useMemo(() => {
  if (!busySet || busySet.size === 0) {
    // Nessuna produzione: 0 giorni lavorati, liberi = giorni trascorsi nell'anno fino ad oggi
    const startOfYear = new Date(currentYear, 0, 1);
    const daysSoFar = Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
    return { workedDays: 0, freeDays: daysSoFar };
  }

  // Conta i giorni dall’inizio anno ad oggi
  let w = 0;
  let f = 0;
  const start = new Date(currentYear, 0, 1);
  for (let d = new Date(start); d <= now; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const key = `${y}-${m}-${day}`;
    if (busySet.has(key)) w += 1;
    else f += 1;
  }
  return { workedDays: w, freeDays: f };
}, [busySet, currentYear]);

const nextGapDate = useMemo(() => {
  // Cerca la prima data futura NON occupata
  const probe = new Date(now);
  for (let i = 0; i < 370; i++) {
    const y = probe.getFullYear();
    const m = `${probe.getMonth() + 1}`.padStart(2, '0');
    const d = `${probe.getDate()}`.padStart(2, '0');
    const key = `${y}-${m}-${d}`;
    if (!busySet || !busySet.has(key)) {
      return new Date(probe);
    }
    probe.setDate(probe.getDate() + 1);
  }
  return null;
}, [busySet, now]);

const nextGapDateStr = nextGapDate
  ? nextGapDate.toLocaleDateString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit' })
  : null;


  const displayName = useMemo(() => displayNameOf(me), [me]);

  const onCreateSaved = async () => {
  setCreateOpen(false);

  try {
    const list = await fetchMyProductionsSmart(fetchJson);
    const normalized = Array.isArray(list)
      ? list.map(normalizeProduction)
      : [];

    const deduped = Array.from(
      new Map(normalized.map(x => [x.id, x])).values()
    );

    setProduzioni(deduped.sort(sortByEndDesc));
    setSnack(t('profile.snack_created'));
  } catch (e) {
    console.error(e);
    setSnack(t('profile.snack_refresh_error'));
  }
};

const onEditSaved = async () => {
  setEditProd(null);

  try {
    const list = await fetchMyProductionsSmart(fetchJson);
    const normalized = Array.isArray(list)
      ? list.map(normalizeProduction)
      : [];

    const deduped = Array.from(
      new Map(normalized.map(x => [x.id, x])).values()
    );

    setProduzioni(deduped.sort(sortByEndDesc));
    setSnack(t('profile.snack_updated'));
  } catch (e) {
    console.error(e);
    setSnack(t('profile.snack_refresh_error'));
  }
};

  const onToggleVisible = async (checked) => {
    setVisible(checked);
    try {
      await fetchJson('/freelancers/self', {
        method: 'PUT',
        body: JSON.stringify({ visible: checked })
      });
      setSnack(checked ? t('profile.snack_visible') : t('profile.snack_hidden'));
    } catch (e) {
      setVisible(v => !v);
      console.error(e);
      setSnack(t('profile.snack_error_visibility'));
    }
  };

  const onAskDelete = async (p) => {
    if (!window.confirm(`${t('profile.productions_confirm_delete')} "${p?.produzione || p?.titolo || p?.title || p?.nome || p?.name || '—'}"?`)) return;
    try {
      await fetchJson(`/maestranze/${p.id}`, { method: 'DELETE' });
      setProduzioni(prev => prev.filter(x => x.id !== p.id));
      setSnack(t('profile.snack_deleted')); 
    } catch (e) {
      console.error(e);
      setSnack(t('profile.snack_error_delete'));
    }
  };

  // ===== Helpers locali =====
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

const daysOfYear = (year) => {
  const start = new Date(year, 0, 1);
  const end = new Date(year + 1, 0, 1);
  const out = [];
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
};

const computeYearStats = (busySet, year) => {
  let worked = 0;
  let free = 0;
  for (const d of daysOfYear(year)) {
    if (busySet.has(ymd(d))) worked++;
    else free++;
  }
  return { workedDays: worked, freeDays: free };
};

const findNextGap = (busySet, fromDate) => {
  // cerca il primo giorno libero dalla data indicata (entro ~2 anni)
  const maxDays = 730;
  const d = new Date(fromDate);
  for (let i = 0; i < maxDays; i++) {
    const key = ymd(d);
    if (!busySet.has(key)) return new Date(d);
    d.setDate(d.getDate() + 1);
  }
  return null;
};

  if (authError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('profile.auth_error')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography>{t('profile.loading')}</Typography>
      </Box>
    );
  }

  if (!me) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t('profile.load_error')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, px: { xs: 2, md: 3 }, py: 2 }}>
      {/* GlobalStyles */}
      <GlobalStyles styles={{
        'html, body': {
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        }
      }} />

      {/* Novità piattaforma — si chiude automaticamente */}
      <NewsBanner />
      
{/* CALENDARIO + STATISTICHE */}
<Grid container spacing={2} sx={{ mt: 0, alignItems: 'stretch' }}>
  {/* SX: Calendario */}
<Grid item xs={12} md={6} sx={{ display: 'flex' }}>
 <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', flex: 1 }}>
  <MiniMonthCalendar busySet={busySet} />
</Box>

</Grid>


    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
    <BachecaBoard />
  </Grid>
</Grid>




      {/* Network stats — dati già caricati, no fetch aggiuntivi */}
      <StatsBanner produzioni={produzioni} workedDays={workedDays} freeDays={freeDays} occupiedDays={occupiedDays} nextGapDate={nextGapDateStr} profileViews={profileViews} />

      {/* LAYOUT 2 colonne */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
          gap: 3,
          alignItems: 'stretch',
        }}
      >
        <LeftCol
          me={me}
          visible={visible}
          onToggleVisible={onToggleVisible}
          onOpenEdit={() => setEditOpen(true)}
        />

        <RightCol
          visible={visible}
          isIncomplete={!((me?.ruoli?.length > 0) && produzioni?.length > 0)}
          me={me}
          onOpenEdit={() => setEditOpen(true)}
          skills={me?.skills || []}
          produzioni={produzioni}
          prodLoading={prodLoading}
          activeProd={produzioni[0]}
          setCreateOpen={setCreateOpen}
          setEditProd={setEditProd}
          onAskDelete={onAskDelete}
          roles={me?.ruoli || (me?.ruolo ? [me.ruolo] : [])}
          overlineSx={overlineSx}
          creditsMap={creditsMap}
        />
      </Box>

      {/* Dialog: Modifica profilo */}
<Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
  <Box sx={{ p: 2 }}>
    <EditProfileForm
      onCancel={() => setEditOpen(false)}
      onProfileUpdated={(saved) => {
        // chiudi la modale
        setEditOpen(false);

        // se il form non ha ritornato nulla, non fare nulla
        if (!saved) return;

        // aggiorna il profilo mostrato in pagina
        setMe((prev) => ({
          ...prev,
          ...saved,
          // garantisci entrambe le chiavi così la BIO appare anche a sinistra
          bio: saved.bio ?? saved.descrizione ?? prev?.bio ?? '',
          descrizione: saved.descrizione ?? saved.bio ?? prev?.descrizione ?? '',
          // aggiorna anche le altre cose che la modale può aver cambiato
          skills: Array.isArray(saved.skills) ? saved.skills : (prev?.skills || []),
          ruoli: Array.isArray(saved.ruoli) ? saved.ruoli : (prev?.ruoli || []),
          profile_image_url: saved.profile_image_url ?? prev?.profile_image_url,
          telefono: saved.telefono ?? prev?.telefono,
          nome: saved.nome ?? prev?.nome,
          cognome: saved.cognome ?? prev?.cognome,
        }));
      }}
    />
  </Box>
</Dialog>

{/* Dialog: Crea produzione */}
<Dialog
  open={createOpen}
  onClose={() => setCreateOpen(false)}
  maxWidth="md"
  fullWidth
>
  <Box sx={{ p: 2 }}>
    <CreateMaestranzaForm
      onCancel={() => setCreateOpen(false)}
      onSaved={onCreateSaved}
    />
  </Box>
</Dialog>

{/* Dialog: Modifica produzione */}
<Dialog
  open={!!editProd}
  onClose={() => setEditProd(null)}
  maxWidth="md"
  fullWidth
>
  <Box sx={{ p: 2 }}>
    <EditMaestranzaForm
      maestranza={editProd}
      onCancel={() => setEditProd(null)}
      onSaved={onEditSaved}
    />
  </Box>
</Dialog>


      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </Box>
  );
}

// ---------------- LeftCol ----------------
function LeftCol({ me, visible, onToggleVisible, onOpenEdit }) {
  const { t } = useTranslation();
  const displayName = displayNameOf(me);
  const avatarUrl = resolveProfileImageUrl(me?.profile_image_url);
  const ruoli = Array.isArray(me?.ruoli) ? me.ruoli : (me?.ruolo ? [me.ruolo] : []);

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '12px',
        backgroundColor: '#111118',
        display: 'flex', flexDirection: 'column', gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          src={avatarUrl}
          alt={displayName}
          sx={{ width: 48, height: 48, flexShrink: 0 }}
          imgProps={{ referrerPolicy: 'no-referrer' }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#e8e6df', lineHeight: 1.2 }}>{displayName}</Typography>
          {ruoli.length > 0 ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
              {ruoli.join(' · ')}
            </Typography>
          ) : (
            <EmptyInline icon={<StyleIcon fontSize="inherit" />} text={t('profile.roles_empty')} />
          )}
        </Box>
      </Box>

      {me?.telefono ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Tel: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12 }}>{formatPhone(me.telefono)}</Box>
        </Typography>
      ) : (
        <EmptyInline icon={<InboxIcon fontSize="inherit" />} text={t('profile.phone_empty')} />
      )}

      {/* === BIO subito sotto il telefono === */}
{(() => {
  const bioText = (typeof me?.bio === 'string' ? me.bio :
                  (typeof me?.descrizione === 'string' ? me.descrizione : '')).trim();

  if (bioText) {
    return (
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>{t('profile.bio_label')}</Typography>
        <Typography sx={{ whiteSpace: 'pre-line', mt: 0.25, fontSize: 12, color: 'text.secondary' }}>
          {bioText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>{t('profile.bio_label')}</Typography>
      <Typography sx={{ color: 'text.disabled', mt: 0.25, fontSize: 12 }}>
        {t('profile.bio_empty')}
      </Typography>
    </Box>
  );
})()}


     {/* visibilità */}
<Box>
  <Stack spacing={1} alignItems="flex-start">
    <Tooltip title={t('profile.visibility_tooltip')}>
      <Chip
        icon={visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
        label={visible ? t('profile.visibility_visible') : t('profile.visibility_hidden')}
        color={visible ? 'success' : 'default'}
        variant={visible ? 'filled' : 'outlined'}
        clickable
        onClick={() => onToggleVisible(!visible)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleVisible(!visible);
          }
        }}
        role="button"
        aria-pressed={visible}
        tabIndex={0}
        sx={{
          fontWeight: 600,
          height: 24,
          fontSize: 11,
          '& .MuiChip-label': { fontSize: 11, px: 1 },
        }}
      />
    </Tooltip>
  </Stack>
</Box>



    {/* === SKILL === */}
<Box sx={{ width: '100%' }}>
  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
    {t('profile.skills_label')}
  </Typography>

  {Array.isArray(me?.skills) && me.skills.length > 0 ? (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 1,   // spazio orizzontale tra chip
        rowGap: 1,      // spazio verticale tra righe
        mt: 1
      }}
    >
      {me.skills.map((s, i) => (
        <Chip
          key={`${s}-${i}`}
          label={s}
          size="small"
          variant="outlined"
          sx={{
            px: 0.75,
            height: 20,
            fontSize: 11,
            borderColor: 'rgba(212,165,80,0.3)',
            color: '#d4a550',
            bgcolor: 'rgba(212,165,80,0.08)',
          }}
        />
      ))}
    </Box>
  ) : (
    <Box sx={{ mt: 1 }}>
      <EmptyInline
        icon={<StyleIcon fontSize="inherit" />}
        text={t('profile.skills_add')}
      />
      <Box sx={{ mt: 1 }}>
        <Button size="small" variant="outlined" onClick={onOpenEdit}>
          {t('profile.skills_btn')}
        </Button>
      </Box>
    </Box>
  )}
</Box>


    </Box>
  );
}

// ---------------- RightCol ----------------
function RightCol({ me, onOpenEdit, skills, produzioni, prodLoading, activeProd, setCreateOpen, setEditProd, onAskDelete, roles, overlineSx, visible, isIncomplete, creditsMap }) {
  const { t } = useTranslation();

  // Stato per il modal "Vedi squadra" inline
  const [squadraOpen, setSquadraOpen]           = React.useState(false);
  const [squadraEditionId, setSquadraEditionId] = React.useState(null);
  const [squadraProgramName, setSquadraProgramName] = React.useState('');
  const [squadraEditionLabel, setSquadraEditionLabel] = React.useState('');

  const handleOpenSquadra = (credit) => {
    setSquadraEditionId(credit.edition_id);
    setSquadraProgramName(credit.program_name || '');
    setSquadraEditionLabel(credit.edition_label || '');
    setSquadraOpen(true);
  };

  return (
    <>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      {/* COLONNA SINISTRA: Skill + Produzioni */}
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: UI.radius,
          backgroundColor: '#111118',
          boxShadow: UI.shadow,
          backdropFilter: 'saturate(120%) blur(2px)',
          order: { xs: 2, md: 1 },
          transition: 'transform .15s ease, box-shadow .2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.18)' },
        }}
      >


        {/* PRODUZIONI */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
              {t('profile.productions_label')}
            </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setCreateOpen(true)}
            sx={{ borderColor: 'rgba(212,165,80,0.4)', color: '#d4a550', fontSize: 12, fontWeight: 600, borderRadius: '6px', px: 1.5, py: 0.5, '&:hover': { background: 'rgba(212,165,80,0.08)', borderColor: '#d4a550' } }}
          >
            {t('profile.productions_add')}
          </Button>
        </Box>

        {prodLoading ? (
          <Typography variant="body2" color="text.secondary">{t('profile.productions_loading')}</Typography>
        ) : (
          produzioni.length === 0 ? (
            <EmptyState
              icon={<InboxIcon />}
              title={t('profile.productions_empty_title')}
              subtitle={t('profile.productions_empty_sub')}
              action={<Button size="small" onClick={() => setCreateOpen(true)}>{t('profile.productions_empty_btn')}</Button>}
            />
          ) : (
            <List sx={{ mt: 1, pl: 0 }}>
              {[activeProd, ...produzioni.filter(p => p !== activeProd)].filter(Boolean).map((p, idx, arr) => {
                const startStr = formatDateIT(
  p.start_date || p.data_inizio || p.data_inizio_contratto || p.inizio || p.da || p.from
);
const endStr   = formatDateIT(
  p.end_date   || p.data_fine   || p.data_fine_contratto   || p.fine   || p.a   || p.to
);
const rangeStr = (startStr && endStr) ? `${startStr} → ${endStr}` : (endStr || startStr || '');

                const isFirst = idx === 0;
                const isLast  = idx === arr.length - 1;

                // Credito CV collegato a questa voce maestranza
                const credit = (creditsMap && p.id != null) ? creditsMap[String(p.id)] : undefined;
                const creditLabel = credit
                  ? [credit.program_name, credit.edition_label].filter(Boolean).join(' · ed. ')
                  : null;
                // Mostra il nome canonico solo se diverso dal testo grezzo della voce
                const rawTitle = (p.produzione || p.titolo || p.title || p.nome || p.name || '').toLowerCase().trim();
                const showCanonical = creditLabel && credit.program_name &&
                  rawTitle !== credit.program_name.toLowerCase().trim();
                const teamCount = credit?.team_count ?? 0;

                return (
                  <ListItem
                    key={p.id || `${p.produzione}-${endStr || idx}`}
                    disableGutters
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr',
                      columnGap: 2,
                      alignItems: 'flex-start',
                      px: 0,
                      py: 1.5,
                      position: 'relative',
                    }}
                  >
                    {/* COLONNA TIMELINE */}
                    <Box sx={{ position: 'relative', height: '100%' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          top: isFirst ? 18 : -24,
                          bottom: isLast ? 24 : -24,
                          width: 2,
                          background: 'linear-gradient(180deg, rgba(255,166,0,0.35), rgba(255,106,0,0.18))',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          top: 14,
                          width: 10, height: 10, borderRadius: '50%',
                          background: 'linear-gradient(180deg, #FFB300, #FF6A00)',
                          boxShadow: '0 0 0 3px rgba(255,166,0,0.18)',
                          zIndex: 1,
                        }}
                      />
                    </Box>

                    

                    {/* COLONNA CONTENUTO */}
                    <ListItemText
                      primary={
  <Box sx={{ display:'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
    {(p.azienda || p.client || p.cliente || p.company || p.network) && (
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>
        {p.azienda || p.client || p.cliente || p.company || p.network}
      </Typography>
    )}

    <Typography
      sx={{ fontWeight: 600, fontSize: 13, color: '#e8e6df' }}
      title={credit ? t('crediti.inserito_come', { text: p.produzione || p.titolo || p.title || p.nome || p.name || '' }) : undefined}
    >
      {credit ? creditLabel : (p.produzione || p.titolo || p.title || p.nome || p.name || '—')}
      {p.is_tentative && (
        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#a78bfa', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: 10, padding: '1px 8px', verticalAlign: 'middle' }}>
          Non confermata
        </span>
      )}
    </Typography>

    {(p.ruolo || p.role || p.mansione) && (
      <Typography sx={{ fontSize: 11, fontStyle:'italic', color:'text.secondary' }}>
        / {p.ruolo || p.role || p.mansione}
      </Typography>
    )}
  </Box>
}
                      secondary={
                        <Box component="div" sx={{ mt: 0.5 }}>
                          {/* Riga date + azioni modifica/elimina */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            {rangeStr && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {startStr && endStr
                                  ? t('profile.productions_period')
                                  : (endStr ? t('profile.productions_end') : t('profile.productions_start'))}{' '}
                                {rangeStr}
                              </Typography>
                            )}

                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 11 }}>·</Box>
                            <Box
                              component="span"
                              onClick={() => setEditProd(toMaestranzaModel(p))}
                              sx={{ fontSize: 11, color: 'rgba(212,165,80,0.6)', cursor: 'pointer', '&:hover': { color: '#d4a550' }, fontWeight: 600 }}
                            >
                              {t('profile.productions_edit')}
                            </Box>
                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 11 }}>·</Box>
                            <Box
                              component="span"
                              onClick={() => onAskDelete(p)}
                              sx={{ fontSize: 11, color: 'rgba(248,113,113,0.4)', cursor: 'pointer', '&:hover': { color: '#f87171' }, fontWeight: 600 }}
                            >
                              {t('profile.productions_delete')}
                            </Box>
                            {credit && credit.role_verified_by === 'production' && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                                background: 'rgba(212,165,80,0.13)', border: '1px solid rgba(212,165,80,0.28)',
                                color: '#d4a550', whiteSpace: 'nowrap', letterSpacing: '0.04em',
                              }}>
                                {t('crediti.verified_production')}
                              </span>
                            )}
                            {credit && credit.role_verified_by === 'coordinator' && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                                background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.3)',
                                color: '#94a3b8', whiteSpace: 'nowrap', letterSpacing: '0.04em',
                              }}>
                                {t('crediti.verified_coordinator')}
                              </span>
                            )}
                            {credit && teamCount >= 1 && (
                              <Box
                                component="span"
                                onClick={() => handleOpenSquadra(credit)}
                                sx={{
                                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  color: 'rgba(212,165,80,0.7)',
                                  border: '1px solid rgba(212,165,80,0.28)',
                                  borderRadius: '6px', px: 1, py: 0.25,
                                  '&:hover': { color: '#d4a550', background: 'rgba(212,165,80,0.08)' },
                                }}
                              >
                                {teamCount > 1
                                  ? t('crediti.vedi_squadra_n', { count: teamCount })
                                  : t('crediti.vedi_squadra')}
                              </Box>
                            )}
                          </Stack>

                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )
        )}
      </Box>

      {/* COLONNA DESTRA: Alert pertinenti */}
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: UI.radius,
          backgroundColor: '#111118',
          boxShadow: UI.shadow,
          backdropFilter: 'saturate(120%) blur(2px)',
          display: 'flex',
          flexDirection: 'column',
          order: { xs: 1, md: 2 },
          transition: 'transform .15s ease, box-shadow .2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.18)' },
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
          {t('profile.notifications_label')}
        </Typography>

        <Box
          sx={{
            flex: 1, minHeight: 0, overflow: 'auto',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.18)', borderRadius: 8 },
          }}
        >
          {/* Alert recenti */}
{(visible === false || isIncomplete) ? (
  <Box sx={{ p: 3, textAlign: 'center', border: '1px solid rgba(212,165,80,0.2)', borderRadius: 2, background: 'rgba(212,165,80,0.05)' }}>
    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔒</div>
    <div style={{ color: '#d4a550', fontFamily: 'Kanit, sans-serif', fontSize: '13px', letterSpacing: '0.05em', marginBottom: '6px' }}>
      {visible === false ? t('profile.profile_hidden_title') : t('profile.profile_incomplete_title')}
    </div>
    <div style={{ color: '#7a7f96', fontSize: '12px' }}>
      {visible === false
        ? 'Rendi il tuo profilo visibile per accedere agli alert.'
        : 'Aggiungi almeno un ruolo e una produzione per accedere agli alert.'}
    </div>
  </Box>
) : (
  <FreelancerAlertsPanel
    roles={roles}
    token={localStorage.getItem('token')}
  />
)}
        <EventiWidget />
        </Box>
      </Box>
    </Box>

    {/* Modal "Vedi squadra" per i crediti CV */}
    <SquadraModal
      open={squadraOpen}
      onClose={() => setSquadraOpen(false)}
      editionId={squadraEditionId}
      programName={squadraProgramName}
      editionLabel={squadraEditionLabel}
    />
    </>
  );
}


// ---------- EventiWidget ----------
function EventiWidget() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [events, setEvents] = React.useState([]);
  const nav = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('jkl_token') || localStorage.getItem('token');
    fetch('/api/events/week', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => {});
  }, []);

  if (!events.length) return null;

  function formatDate(d) {
    return new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { weekday:'short', day:'numeric', month:'short' });
  }

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid rgba(212,165,80,0.2)', background: 'rgba(212,165,80,0.04)' }}>
      <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#d4a550', mb: 1.5 }}>
        {t('profile.events_label')}
      </Box>
      {events.map(e => (
        <Box key={e.id} onClick={() => nav(`/eventi/${e.id}`)}
          sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', cursor:'pointer', '&:last-child': { borderBottom: 'none' }, '&:hover': { opacity: 0.8 } }}>
          <Box>
            <Box sx={{ fontSize: 13, fontWeight: 600, color: '#e8e6df' }}>{e.title}</Box>
            <Box sx={{ fontSize: 11, color: 'rgba(232,230,223,0.45)' }}>{formatDate(e.event_date)}{e.city ? ` · ${e.city}` : ''}</Box>
          </Box>
          <Box sx={{ fontSize: 11, color: Number(e.is_joined) === 1 ? '#4ade80' : '#d4a550', flexShrink: 0, ml: 1 }}>
            {Number(e.is_joined) === 1 ? t('profile.events_joined') : '→'}
          </Box>
        </Box>
      ))}
      <Box onClick={() => nav('/eventi')} sx={{ fontSize: 12, color: '#d4a550', mt: 1.5, cursor: 'pointer', textAlign: 'right' }}>
        {t('profile.events_all')}
      </Box>
    </Box>
  );
}


// ---------- EventiWidget ----------

// ---------- Empty state components ----------
function EmptyState({ icon, title, subtitle, action }) {
  return (
    <Stack
      spacing={1}
      alignItems="center"
      sx={{
        py: 3,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Box sx={{ fontSize: 22, opacity: 0.7 }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {subtitle}
        </Typography>
      )}
      {action}
    </Stack>
  );
}

function EmptyInline({ icon, text }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', fontSize: 13 }}>
      <Box sx={{ fontSize: '1.1em', opacity: 0.75 }}>{icon}</Box>
      <span>{text}</span>
    </Box>
  );
}

// ─── Palette locale per CreditiSection (identica a MieiCreditiPage) ───────────
const CV_GOLD        = '#d4a550';
const CV_GOLD_DIM    = 'rgba(212,165,80,0.13)';
const CV_GOLD_BORDER = 'rgba(212,165,80,0.28)';
const CV_BORDER      = 'rgba(255,255,255,0.07)';
const CV_TEXT        = '#e8e6df';
const CV_TEXT_DIM    = 'rgba(232,230,223,0.5)';
const CV_BG2         = '#13131f';
const CV_BG3         = '#1a1a28';
const CV_RED_DIM     = 'rgba(248,113,113,0.13)';
const CV_RED         = '#f87171';
const CV_SILVER_DIM  = 'rgba(148,163,184,0.12)';
const CV_SILVER      = '#94a3b8';

// ─── Helper: formatta range periodo credito ────────────────────────────────────
const IT_MON = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const EN_MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtCreditRange(selfStart, selfEnd, year, lang = 'it') {
  const MON = lang === 'en' ? EN_MON : IT_MON;
  const parse = (iso) => {
    if (!iso) return null;
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d) ? null : d;
  };
  const s = parse(selfStart);
  const e = parse(selfEnd);
  if (s && e) {
    const sy = s.getFullYear(), sm = MON[s.getMonth()];
    const ey = e.getFullYear(), em = MON[e.getMonth()];
    if (sy === ey && sm === em) return `${sm} ${sy}`;
    if (sy === ey) return `${sm} – ${em} ${sy}`;
    return `${sm} ${sy} – ${em} ${ey}`;
  }
  if (s) return `${MON[s.getMonth()]} ${s.getFullYear()}`;
  if (year) return String(year);
  return '';
}

// ─── Badge verifica ────────────────────────────────────────────────────────────
function CreditVerifiedBadge({ roleVerifiedBy, t }) {
  if (roleVerifiedBy === 'production') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        background: CV_GOLD_DIM, border: `1px solid ${CV_GOLD_BORDER}`, color: CV_GOLD,
        whiteSpace: 'nowrap', letterSpacing: '0.04em',
      }}>
        {t('crediti.verified_production')}
      </span>
    );
  }
  if (roleVerifiedBy === 'coordinator') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        background: CV_SILVER_DIM, border: `1px solid rgba(148,163,184,0.3)`, color: CV_SILVER,
        whiteSpace: 'nowrap', letterSpacing: '0.04em',
      }}>
        {t('crediti.verified_coordinator')}
      </span>
    );
  }
  return null;
}

// ─── Sezione "I miei crediti" da mostrare nel profilo freelance ────────────────
function CreditiSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  const [credits, setCredits]   = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [error, setError]       = React.useState(null);

  // Stato modal squadra
  const [modalOpen, setModalOpen]           = React.useState(false);
  const [modalEditionId, setModalEditionId] = React.useState(null);
  const [modalProgramName, setModalProgramName] = React.useState('');
  const [modalEditionLabel, setModalEditionLabel] = React.useState('');

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchJson('/cv/me/credits')
      .then(data => {
        if (!alive) return;
        setCredits(Array.isArray(data?.credits) ? data.credits : []);
        setError(null);
      })
      .catch(() => {
        if (!alive) return;
        setError(t('crediti.crediti_error'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  const handleOpenSquadra = (credit) => {
    setModalEditionId(credit.edition_id);
    setModalProgramName(credit.program_name || '');
    setModalEditionLabel(credit.edition_label || '');
    setModalOpen(true);
  };

  return (
    <>
      <Box sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: UI.radius,
        backgroundColor: '#111118',
        boxShadow: UI.shadow,
      }}>
        {/* Intestazione sezione */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'text.secondary',
          }}>
            {t('crediti.sezione_titolo')}
          </Typography>
        </Box>

        {/* Loading */}
        {loading && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('crediti.crediti_loading')}
          </Typography>
        )}

        {/* Errore */}
        {!loading && error && (
          <div style={{
            background: CV_RED_DIM, border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: 8, padding: '10px 14px', color: CV_RED, fontSize: 12,
          }}>
            {error}
          </div>
        )}

        {/* Nessun credito */}
        {!loading && !error && credits.length === 0 && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
            {t('crediti.nessun_credito')}
          </Typography>
        )}

        {/* Lista crediti */}
        {!loading && !error && credits.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {credits.map((credit) => {
              const periodo = fmtCreditRange(credit.self_start, credit.self_end, credit.year, lang);
              const teamCount = credit.team_count ?? 0;

              return (
                <div
                  key={credit.id}
                  style={{
                    background: CV_BG2,
                    border: `1px solid ${CV_BORDER}`,
                    borderRadius: 10,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {/* Riga principale: programma + edizione */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: CV_TEXT,
                    }}>
                      {credit.program_name || '—'}
                    </span>
                    {credit.edition_label && (
                      <span style={{ fontSize: 11, color: CV_TEXT_DIM }}>
                        {credit.edition_label}
                      </span>
                    )}
                  </div>

                  {/* Riga secondaria: ruolo + badge + periodo */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: 8, flexWrap: 'wrap',
                  }}>
                    {credit.role && (
                      <span style={{
                        fontSize: 11, padding: '2px 10px', borderRadius: 20,
                        background: CV_GOLD_DIM, border: `1px solid ${CV_GOLD_BORDER}`,
                        color: CV_GOLD,
                      }}>
                        {credit.role}
                      </span>
                    )}
                    <CreditVerifiedBadge
                      roleVerifiedBy={credit.role_verified_by}
                      t={t}
                    />
                    {periodo && (
                      <span style={{ fontSize: 11, color: CV_TEXT_DIM }}>
                        {periodo}
                      </span>
                    )}
                  </div>

                  {/* Bottone "Vedi squadra" */}
                  {teamCount >= 1 && (
                    <div>
                      <button
                        onClick={() => handleOpenSquadra(credit)}
                        style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '5px 12px', borderRadius: 8,
                          background: 'transparent',
                          border: `1px solid ${CV_GOLD_BORDER}`,
                          color: CV_GOLD, cursor: 'pointer',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = CV_GOLD_DIM; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {teamCount > 1
                          ? t('crediti.vedi_squadra_n', { count: teamCount })
                          : t('crediti.vedi_squadra')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Box>

      {/* Modal squadra */}
      <SquadraModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editionId={modalEditionId}
        programName={modalProgramName}
        editionLabel={modalEditionLabel}
      />
    </>
  );
}